import { useState, useRef, useEffect } from 'react';
import { X, ExternalLink, MessageSquare, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useChangeRequests } from '../../context/ChangeRequestProvider';

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  bg: {
    page: '#0e0e0e',
    card: '#18181b',
    surface: '#27272a',
    raised: '#3f3f46',
  },
  text: {
    primary: '#f4f4f5',
    secondary: '#a1a1aa',
    muted: '#52525b',
    danger: '#ef4444',
    warning: '#f59e0b',
    success: '#22c55e',
    info: '#3b82f6',
    purple: '#a78bfa',
  },
  badge: {
    high: {
      bg: 'rgba(239,68,68,0.12)',
      text: '#ef4444',
      border: 'rgba(239,68,68,0.3)',
    },
    medium: {
      bg: 'rgba(245,158,11,0.12)',
      text: '#f59e0b',
      border: 'rgba(245,158,11,0.3)',
    },
    low: {
      bg: 'rgba(34,197,94,0.12)',
      text: '#22c55e',
      border: 'rgba(34,197,94,0.3)',
    },
    info: {
      bg: 'rgba(59,130,246,0.12)',
      text: '#3b82f6',
      border: 'rgba(59,130,246,0.3)',
    },
    purple: {
      bg: 'rgba(139,92,246,0.12)',
      text: '#a78bfa',
      border: 'rgba(139,92,246,0.3)',
    },
  },
  border: '#27272a',
  borderSubtle: '#18181b',
  radius: { sm: 4, md: 8, lg: 12 },
  mono: "'JetBrains Mono','Fira Code','Cascadia Code',monospace",
  sans: "'DM Sans','Outfit',system-ui,sans-serif",
};

// ─── Default impact data (used when cr.impact is absent) ─────────────────────
const DEFAULT_TASKS = [
  {
    id: 'T-19',
    name: 'Invoice data model design',
    owner: 'James R',
    status: 'In progress',
    impact: 'Model must add currency fields — direct redesign',
    severity: 'high',
  },
  {
    id: 'T-22',
    name: 'API layer — invoice module',
    owner: 'James R',
    status: 'Not started',
    impact: 'Depends entirely on T-19 output',
    severity: 'high',
  },
  {
    id: 'T-24',
    name: 'Invoice PDF generation',
    owner: 'Priya S',
    status: 'Not started',
    impact: 'Currency symbol and formatting logic changes',
    severity: 'medium',
  },
  {
    id: 'T-28',
    name: 'Integration — Xero export',
    owner: 'Dev team',
    status: 'Not started',
    impact: 'Xero mapping must handle multi-currency fields',
    severity: 'high',
  },
  {
    id: 'T-31',
    name: 'UAT scripts — invoicing',
    owner: 'Anita K',
    status: 'Not started',
    impact: 'All test cases written for GBP only — full rewrite needed',
    severity: 'high',
  },
  {
    id: 'T-34',
    name: 'UAT execution',
    owner: 'Anita K',
    status: 'Not started',
    impact: 'Delayed until T-31 rewrite is complete',
    severity: 'medium',
  },
  {
    id: 'T-38',
    name: 'Go live checklist',
    owner: 'PM',
    status: 'Not started',
    impact: 'Dependent on T-34 sign-off',
    severity: 'medium',
  },
];
const DEFAULT_TASK_VERIFY = [
  {
    person: 'James R',
    action: 'Confirm effort for T-19 data model redesign (est: 18–24 hrs)',
  },
  {
    person: 'Anita K',
    action: 'Confirm UAT rewrite estimate for T-31 (est: 14–18 hrs)',
  },
  {
    person: 'Dev team',
    action: 'Confirm Xero multi-currency API feasibility for T-28',
  },
];
const DEFAULT_MODULES = [
  {
    name: 'Invoicing module',
    centrality: 'Core',
    downstream: 6,
    impact: 'Direct redesign — data model, logic, UI',
    severity: 'high',
  },
  {
    name: 'API layer',
    centrality: 'High',
    downstream: 4,
    impact: 'Invoice API contracts must be updated for currency fields',
    severity: 'high',
  },
  {
    name: 'Reporting module',
    centrality: 'Medium',
    downstream: 2,
    impact: 'All invoice reports must handle multi-currency display',
    severity: 'medium',
  },
  {
    name: 'Integration (Xero)',
    centrality: 'High',
    downstream: 1,
    impact: 'Xero export requires vendor API changes outside current scope',
    severity: 'high',
  },
  {
    name: 'UAT framework',
    centrality: 'Low',
    downstream: 0,
    impact: 'All invoicing test suites need full rewrite',
    severity: 'medium',
  },
];
const DEFAULT_MODULE_VERIFY = [
  {
    person: 'James R (tech lead)',
    action: 'Confirm scope of API contract changes required',
  },
  {
    person: 'Anita K (QA lead)',
    action: 'Confirm whether UAT framework requires full or partial rewrite',
  },
];
const DEFAULT_PHASES = [
  {
    phase: 'Build (Phase 2)',
    schedule: 'Week 6 of 12',
    impact: '3–4 day critical path delay — James R is gating',
    delta: '+3–4d',
    severity: 'high',
  },
  {
    phase: 'Testing (Phase 3)',
    schedule: 'Week 9–11',
    impact:
      'Cannot start until UAT rewrite complete — cascades from build delay',
    delta: '+8–11d',
    severity: 'high',
  },
  {
    phase: 'Go live (Phase 4)',
    schedule: 'Week 12',
    impact:
      'Buffer (5d) insufficient — net overrun risk after buffer exhaustion',
    delta: '+3–6d',
    severity: 'high',
  },
];
const DEFAULT_PHASES_VERIFY = [
  {
    person: 'Priya S (delivery lead)',
    action: 'Confirm critical path extension and rebalancing options',
  },
  {
    person: 'PM',
    action: 'Confirm go live date exposure with client before call',
  },
];
const DEFAULT_STAKEHOLDERS = [
  {
    name: 'Finance Director (Client)',
    role: 'Decision maker',
    action: 'Re-sign-off required if invoicing logic changes',
    urgency: 'high',
  },
  {
    name: 'Client legal team',
    role: 'Approver',
    action: 'SOW amendment approval needed for scope addition',
    urgency: 'high',
  },
  {
    name: 'Sarah Mitchell',
    role: 'CR originator',
    action: 'Must be informed of full impact before any commitment',
    urgency: 'medium',
  },
  {
    name: 'Internal delivery lead',
    role: 'Delivery owner',
    action: 'Resource rebalancing decision required immediately',
    urgency: 'high',
  },
];
const DEFAULT_STKH_VERIFY = [
  {
    person: 'Account manager',
    action:
      'Confirm whether Finance Director sign-off is contractually required per SOW §6.1',
  },
];
const DEFAULT_RESOURCES = [
  {
    name: 'James R (tech lead)',
    util: 94,
    impact: 'Cannot absorb without displacing T-31 or T-34',
    severity: 'high',
  },
  {
    name: 'Anita K (QA lead)',
    util: 78,
    impact: 'UAT rewrite pushes utilisation to ~96%',
    severity: 'high',
  },
  {
    name: 'Priya S (delivery lead)',
    util: 70,
    impact: 'Coordination overhead increases by ~4 hrs',
    severity: 'low',
  },
  {
    name: 'Dev team',
    util: 85,
    impact: 'Xero work requires 1 additional dev day',
    severity: 'medium',
  },
];
const DEFAULT_RES_VERIFY = [
  {
    person: 'James R',
    action: 'Confirm availability and whether T-31 or T-34 can be displaced',
  },
  {
    person: 'Resource manager',
    action: 'Confirm if junior developer is available from 12 May',
  },
];
const DEFAULT_BUDGET_ROWS = [
  {
    category: 'James R — data model (T-19)',
    low: 9180,
    high: 12240,
    basis: '18–24 hrs × £510/day',
  },
  {
    category: 'James R — API layer (T-22)',
    low: 3060,
    high: 4080,
    basis: '12–16 hrs × £510/day',
  },
  {
    category: 'Anita K — UAT rewrite (T-31)',
    low: 2800,
    high: 3600,
    basis: '14–18 hrs × £400/day',
  },
  {
    category: 'Dev team — Xero integration',
    low: 850,
    high: 1700,
    basis: '1–2 days blended rate',
  },
];
const DEFAULT_BUDGET_VERIFY = [
  {
    person: 'Tom B (finance)',
    action:
      'Confirm contingency remaining and whether client budget conversation is needed',
  },
  {
    person: 'PM',
    action: 'Confirm if SOW amendment is required before acceptance',
  },
];
const DEFAULT_TIMELINE_ROWS = [
  {
    milestone: 'Build phase end',
    original: '16 May 2026',
    revised: '24–27 May 2026',
    delta: '+8–11d',
    sev: 'warning',
  },
  {
    milestone: 'UAT start',
    original: '19 May 2026',
    revised: '27 May–1 Jun 2026',
    delta: '+8–11d',
    sev: 'warning',
  },
  {
    milestone: 'UAT end',
    original: '2 Jun 2026',
    revised: '10–15 Jun 2026',
    delta: '+8–13d',
    sev: 'warning',
  },
  {
    milestone: 'Go live',
    original: '9 Jun 2026',
    revised: '17–22 Jun 2026',
    delta: '+8–13d',
    sev: 'danger',
  },
];
const DEFAULT_TL_VERIFY = [
  {
    person: 'Priya S',
    action:
      'Confirm critical path and whether parallel working reduces timeline exposure',
  },
];
const DEFAULT_SCENARIOS = [
  {
    id: 1,
    name: 'Accept core only',
    desc: 'Accept data model and API changes. Exclude Xero. Defer UAT rewrite to Phase 2.',
    reductions: [
      { label: 'Timeline', val: 36 },
      { label: 'Budget', val: 41 },
      { label: 'Effort', val: 44 },
    ],
    timeline: '+5–7d',
    budget: '~£9.5K',
    withinContingency: true,
  },
  {
    id: 2,
    name: 'USD only (single addition)',
    desc: 'Add USD as the only new currency. Limits data model to one field set.',
    reductions: [
      { label: 'Timeline', val: 73 },
      { label: 'Budget', val: 65 },
      { label: 'Effort', val: 67 },
    ],
    timeline: '+3–4d',
    budget: '~£6.5K',
    withinContingency: true,
  },
  {
    id: 3,
    name: 'Defer entirely to Phase 2',
    desc: 'Reject current CR. Log as Phase 2 requirement. Architecture team adds a forward-compatibility note only.',
    reductions: [
      { label: 'Timeline', val: 100 },
      { label: 'Budget', val: 96 },
      { label: 'Effort', val: 96 },
    ],
    timeline: '0 days',
    budget: '~£1K',
    withinContingency: true,
    cautionBadge: 'Client relationship risk',
  },
];
const DEFAULT_SIM_COMPONENTS = [
  {
    key: 'dm',
    label: 'Invoice data model redesign',
    effortL: 18,
    effortH: 24,
    tlL: 3,
    tlH: 4,
    budL: 9180,
    budH: 12240,
    risk: 'high',
  },
  {
    key: 'api',
    label: 'API layer — currency fields',
    effortL: 12,
    effortH: 16,
    tlL: 2,
    tlH: 3,
    budL: 6120,
    budH: 8160,
    risk: 'high',
  },
  {
    key: 'xero',
    label: 'Xero integration (multi-currency)',
    effortL: 8,
    effortH: 12,
    tlL: 1,
    tlH: 2,
    budL: 4080,
    budH: 6120,
    risk: 'high',
  },
  {
    key: 'uat',
    label: 'UAT scripts rewrite',
    effortL: 14,
    effortH: 18,
    tlL: 2,
    tlH: 3,
    budL: 5600,
    budH: 7200,
    risk: 'medium',
  },
  {
    key: 'pdf',
    label: 'Invoice PDF — currency formatting',
    effortL: 4,
    effortH: 6,
    tlL: 0.5,
    tlH: 1,
    budL: 2040,
    budH: 3060,
    risk: 'low',
  },
];

// ─── UI Atoms ─────────────────────────────────────────────────────────────────

const Badge = ({ level = 'info', children }) => {
  const c = T.badge[level] || T.badge.info;
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        padding: '2px 8px',
        borderRadius: T.radius.sm,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        fontFamily: T.mono,
        display: 'inline-block',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const MetricCard = ({ label, value, color }) => (
  <div
    style={{
      background: T.bg.surface,
      borderRadius: T.radius.md,
      padding: '10px 12px',
    }}
  >
    <div
      style={{
        fontSize: 10,
        color: T.text.secondary,
        marginBottom: 3,
        fontFamily: T.mono,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 17,
        fontWeight: 500,
        fontFamily: T.mono,
        color: color || T.text.primary,
      }}
    >
      {value}
    </div>
  </div>
);

const BellCurve = ({ confidence, width = 240, height = 60 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width,
      H = cv.height;
    ctx.clearRect(0, 0, W, H);
    const mean = W * 0.5,
      sigma = W * 0.14;
    const cx = Math.min((confidence / 100) * W, W - 2);
    const gauss = (x) =>
      H - H * 0.8 * Math.exp(-0.5 * ((x - mean) / sigma) ** 2);
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (let x = 0; x <= cx; x++) ctx.lineTo(x, gauss(x));
    ctx.lineTo(cx, H);
    ctx.closePath();
    ctx.fillStyle = 'rgba(59,130,246,0.18)';
    ctx.fill();
    ctx.beginPath();
    for (let x = 0; x <= W; x++) {
      if (x === 0) ctx.moveTo(x, gauss(x));
      else ctx.lineTo(x, gauss(x));
    }
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.moveTo(cx, 2);
    ctx.lineTo(cx, H);
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = '11px monospace';
    ctx.fillStyle = '#f59e0b';
    ctx.fillText(`${confidence}%`, cx > W - 38 ? cx - 34 : cx + 4, 14);
  }, [confidence]);
  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{ display: 'block' }}
    />
  );
};

const UtilBar = ({ value }) => {
  const color =
    value >= 90 ? T.text.danger : value >= 75 ? T.text.warning : T.text.success;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{ width: 90, height: 5, background: T.border, borderRadius: 3 }}
      >
        <div
          style={{
            width: `${value}%`,
            height: '100%',
            background: color,
            borderRadius: 3,
          }}
        />
      </div>
      <span
        style={{ fontSize: 12, fontFamily: T.mono, color, fontWeight: 700 }}
      >
        {value}%
      </span>
    </div>
  );
};

const VerificationBox = ({ items }) => {
  const [checked, setChecked] = useState({});
  return (
    <div
      style={{
        background: T.bg.surface,
        borderRadius: T.radius.md,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: T.text.secondary,
          marginBottom: 10,
          fontFamily: T.mono,
          letterSpacing: '0.08em',
        }}
      >
        Verification path
      </div>
      {items.map((v, i) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          key={i}
          onClick={() => setChecked((p) => ({ ...p, [i]: !p[i] }))}
          style={{
            display: 'flex',
            gap: 9,
            padding: '7px 6px',
            borderRadius: T.radius.sm,
            cursor: 'pointer',
            marginBottom: i < items.length - 1 ? 5 : 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = T.bg.raised)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = 'transparent')
          }
        >
          <div
            style={{
              width: 17,
              height: 17,
              borderRadius: 4,
              flexShrink: 0,
              marginTop: 1,
              border: `1.5px solid ${checked[i] ? T.text.success : T.border}`,
              background: checked[i] ? 'rgba(34,197,94,0.15)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.text.success,
              fontSize: 11,
            }}
          >
            {checked[i] ? '✓' : ''}
          </div>
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: T.text.info,
                marginBottom: 1,
              }}
            >
              {v.person}
            </div>
            <div
              style={{ fontSize: 11, color: T.text.secondary, lineHeight: 1.4 }}
            >
              {v.action}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const BottomRow = ({ confidence, source, verify }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12,
      marginTop: 14,
    }}
  >
    <div
      style={{
        background: T.bg.surface,
        borderRadius: T.radius.md,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: T.text.secondary,
          marginBottom: 8,
          fontFamily: T.mono,
        }}
      >
        Confidence — {confidence}%
      </div>
      <BellCurve confidence={confidence} />
      {source && (
        <div style={{ fontSize: 10, color: T.text.secondary, marginTop: 6 }}>
          Based on: {source}
        </div>
      )}
    </div>
    <VerificationBox items={verify} />
  </div>
);

// ─── Table helpers ────────────────────────────────────────────────────────────
const Th = ({ children }) => (
  <th
    style={{
      padding: '6px 10px',
      textAlign: 'left',
      color: T.text.secondary,
      fontSize: 11,
      fontWeight: 500,
      borderBottom: `1px solid ${T.border}`,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </th>
);
const Td = ({ children, mono, color, center }) => (
  <td
    style={{
      padding: '9px 10px',
      borderBottom: `1px solid ${T.borderSubtle}`,
      color: color || T.text.primary,
      fontFamily: mono ? T.mono : undefined,
      fontSize: 12,
      textAlign: center ? 'center' : undefined,
      verticalAlign: 'top',
      lineHeight: 1.4,
    }}
  >
    {children}
  </td>
);
const TR = ({ children, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <tr
      style={{
        background: hover ? T.bg.raised : 'transparent',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

// ─── Tab panels ───────────────────────────────────────────────────────────────
const TasksTab = ({ data, onTaskClick, onChatWithAgent, chatLoading }) => {
  const tasks = data?.tasks || DEFAULT_TASKS;
  const verify = data?.taskVerify || DEFAULT_TASK_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        {tasks.length} tasks impacted
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>Task</Th>
            <Th>Owner</Th>
            <Th>Why impacted</Th>
            <Th>Severity</Th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <TR key={t.id} onClick={() => onTaskClick && onTaskClick(t)}>
              <Td mono color={T.text.info}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t.id} <ExternalLink size={10} />
                </span>
              </Td>
              <Td>{t.name}</Td>
              <Td color={T.text.secondary}>{t.owner}</Td>
              <Td>{t.impact}</Td>
              <Td>
                <Badge level={t.severity === 'high' ? 'high' : 'medium'}>
                  {t.severity === 'high' ? 'High' : 'Medium'}
                </Badge>
              </Td>
            </TR>
          ))}
        </tbody>
      </table>
      <BottomRow
        confidence={85}
        source="Project Plan, predecessor links"
        verify={verify}
      />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const ModulesTab = ({ data, onChatWithAgent, chatLoading }) => {
  const modules = data?.modules || DEFAULT_MODULES;
  const verify = data?.moduleVerify || DEFAULT_MODULE_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        {modules.length} modules impacted
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>Module</Th>
            <Th>Centrality</Th>
            <Th>Downstream</Th>
            <Th>Impact</Th>
            <Th>Severity</Th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m, i) => (
            <TR key={i}>
              <Td>{m.name}</Td>
              <Td>
                <Badge
                  level={
                    m.centrality === 'Core' || m.centrality === 'High'
                      ? 'high'
                      : m.centrality === 'Medium'
                        ? 'medium'
                        : 'low'
                  }
                >
                  {m.centrality}
                </Badge>
              </Td>
              <Td mono center>
                {m.downstream}
              </Td>
              <Td>{m.impact}</Td>
              <Td>
                <Badge level={m.severity === 'high' ? 'high' : 'medium'}>
                  {m.severity === 'high' ? 'High' : 'Medium'}
                </Badge>
              </Td>
            </TR>
          ))}
        </tbody>
      </table>
      <BottomRow
        confidence={82}
        source="Architecture Document"
        verify={verify}
      />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const PhasesTab = ({ data, onPhaseClick, onChatWithAgent, chatLoading }) => {
  const phases = data?.phases || DEFAULT_PHASES;
  const verify = data?.phasesVerify || DEFAULT_PHASES_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        {phases.length} phases affected
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>Phase</Th>
            <Th>Current schedule</Th>
            <Th>Impact</Th>
            <Th>Delta</Th>
            <Th>Severity</Th>
          </tr>
        </thead>
        <tbody>
          {phases.map((p, i) => (
            <TR key={i}>
              <Td>
                <button
                  onClick={() => onPhaseClick && onPhaseClick(p)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: T.text.primary,
                    cursor: 'pointer',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {p.phase}{' '}
                  <ExternalLink size={10} style={{ color: T.text.secondary }} />
                </button>
              </Td>
              <Td color={T.text.secondary}>{p.schedule}</Td>
              <Td>{p.impact}</Td>
              <Td mono color={T.text.warning}>
                {p.delta}
              </Td>
              <Td>
                <Badge level="high">High</Badge>
              </Td>
            </TR>
          ))}
        </tbody>
      </table>
      <div
        style={{
          marginTop: 10,
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: T.radius.md,
          padding: '10px 14px',
          fontSize: 12,
          color: T.text.danger,
        }}
      >
        Buffer: 5 days · CR exposure: 8–11 days · Net overrun risk: 3–6 days
        after buffer exhaustion
      </div>
      <BottomRow confidence={88} source="Project baseline" verify={verify} />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const StakeholdersTab = ({ data, navigate, onChatWithAgent, chatLoading }) => {
  const stakeholders = data?.stakeholders || DEFAULT_STAKEHOLDERS;
  const verify = data?.stkVerify || DEFAULT_STKH_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        {stakeholders.length} stakeholders affected
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>Stakeholder</Th>
            <Th>Role</Th>
            <Th>Required action</Th>
            <Th>Urgency</Th>
          </tr>
        </thead>
        <tbody>
          {stakeholders.map((s, i) => (
            <TR key={i}>
              <Td>
                <button
                  onClick={() => navigate('/communication')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: T.text.primary,
                    cursor: 'pointer',
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {s.name}{' '}
                  <ExternalLink size={10} style={{ color: T.text.secondary }} />
                </button>
              </Td>
              <Td color={T.text.secondary}>{s.role}</Td>
              <Td>{s.action}</Td>
              <Td>
                <Badge level={s.urgency === 'high' ? 'high' : 'medium'}>
                  {s.urgency === 'high' ? 'High' : 'Medium'}
                </Badge>
              </Td>
            </TR>
          ))}
        </tbody>
      </table>
      <BottomRow
        confidence={75}
        source="SOW, Approval Matrix"
        verify={verify}
      />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const ResourcesTab = ({ data, onChatWithAgent, chatLoading }) => {
  const resources = data?.resources || DEFAULT_RESOURCES;
  const verify = data?.resVerify || DEFAULT_RES_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        {resources.length} resources impacted
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {resources.map((r, i) => (
          <div
            key={i}
            style={{
              background: T.bg.surface,
              borderRadius: T.radius.md,
              padding: '12px 14px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 8,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: T.text.primary,
                    marginBottom: 2,
                  }}
                >
                  {r.name}
                </div>
                <div style={{ fontSize: 11, color: T.text.secondary }}>
                  {r.impact}
                </div>
              </div>
              <Badge
                level={
                  r.severity === 'high'
                    ? 'high'
                    : r.severity === 'medium'
                      ? 'medium'
                      : 'low'
                }
              >
                {r.severity.charAt(0).toUpperCase() + r.severity.slice(1)}
              </Badge>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  fontSize: 10,
                  color: T.text.secondary,
                  fontFamily: T.mono,
                  width: 80,
                }}
              >
                Utilisation
              </span>
              <UtilBar value={r.util} />
            </div>
          </div>
        ))}
      </div>
      <BottomRow confidence={91} source="Resource plan" verify={verify} />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const BudgetTab = ({ data, onChatWithAgent, chatLoading }) => {
  const rows = data?.budgetRows || DEFAULT_BUDGET_ROWS;
  const verify = data?.budgetVerify || DEFAULT_BUDGET_VERIFY;
  const totalL = rows.reduce((a, r) => a + r.low, 0);
  const totalH = rows.reduce((a, r) => a + r.high, 0);
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        Source: Budget Tracker
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>Category</Th>
            <Th>Low estimate</Th>
            <Th>High estimate</Th>
            <Th>Basis</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <TR key={i}>
              <Td>{r.category}</Td>
              <Td mono color={T.text.success}>
                £{r.low.toLocaleString()}
              </Td>
              <Td mono color={T.text.danger}>
                £{r.high.toLocaleString()}
              </Td>
              <Td color={T.text.secondary}>{r.basis}</Td>
            </TR>
          ))}
          <tr style={{ background: T.bg.surface }}>
            <td
              style={{
                padding: '10px',
                fontWeight: 500,
                color: T.text.primary,
                fontSize: 12,
              }}
            >
              Total
            </td>
            <td
              style={{
                padding: '10px',
                fontFamily: T.mono,
                fontWeight: 700,
                color: T.text.success,
                fontSize: 14,
              }}
            >
              £{totalL.toLocaleString()}
            </td>
            <td
              style={{
                padding: '10px',
                fontFamily: T.mono,
                fontWeight: 700,
                color: T.text.danger,
                fontSize: 14,
              }}
            >
              £{totalH.toLocaleString()}
            </td>
            <td />
          </tr>
        </tbody>
      </table>
      <div
        style={{
          marginTop: 10,
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: T.radius.md,
          padding: '10px 14px',
          fontSize: 12,
          color: T.text.danger,
        }}
      >
        Contingency remaining: £9,500 · This CR exceeds contingency — client
        commercial conversation required before acceptance
      </div>
      <BottomRow
        confidence={93}
        source="Rate card and actuals"
        verify={verify}
      />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

const TimelineTab = ({ data, navigate, onChatWithAgent, chatLoading }) => {
  const rows = data?.timelineRows || DEFAULT_TIMELINE_ROWS;
  const verify = data?.tlVerify || DEFAULT_TL_VERIFY;
  return (
    <div>
      <div style={{ fontSize: 11, color: T.text.secondary, marginBottom: 10 }}>
        Source: Project baseline
      </div>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
      >
        <thead>
          <tr>
            <Th>Milestone</Th>
            <Th>Original date</Th>
            <Th>Revised if CR accepted</Th>
            <Th>Delta</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <TR key={i}>
              <Td>{r.milestone}</Td>
              <Td mono color={T.text.success}>
                {r.original}
              </Td>
              <Td mono color={T.text.danger}>
                {r.revised}
              </Td>
              <Td
                mono
                color={r.sev === 'danger' ? T.text.danger : T.text.warning}
              >
                {r.delta}
              </Td>
            </TR>
          ))}
        </tbody>
      </table>
      <BottomRow confidence={88} source="Project baseline" verify={verify} />
      <TabChatButton onClick={onChatWithAgent} loading={chatLoading} />
    </div>
  );
};

// ─── Scenarios section ────────────────────────────────────────────────────────
const ScenarioCard = ({ s }) => {
  const [open, setOpen] = useState(false);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={() => setOpen((p) => !p)}
      style={{
        background: open ? T.bg.surface : T.bg.card,
        border: `1px solid ${open ? 'rgba(139,92,246,0.5)' : T.border}`,
        borderRadius: T.radius.md,
        padding: 14,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: T.text.purple,
          fontFamily: T.mono,
          marginBottom: 3,
        }}
      >
        Scenario {s.id}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: T.text.primary,
          marginBottom: 6,
          lineHeight: 1.3,
        }}
      >
        {s.name}
      </div>
      <div
        style={{
          fontSize: 11,
          color: T.text.secondary,
          marginBottom: 10,
          lineHeight: 1.5,
        }}
      >
        {s.desc}
      </div>
      {s.reductions.map((r) => (
        <div key={r.label} style={{ marginBottom: 6 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10,
              color: T.text.secondary,
              marginBottom: 3,
            }}
          >
            <span>{r.label} reduction</span>
            <span style={{ color: T.text.success, fontFamily: T.mono }}>
              −{r.val}%
            </span>
          </div>
          <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
            <div
              style={{
                width: `${r.val}%`,
                height: '100%',
                background: T.text.success,
                borderRadius: 2,
              }}
            />
          </div>
        </div>
      ))}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          marginTop: 10,
        }}
      >
        <div
          style={{
            background: T.bg.raised,
            borderRadius: T.radius.sm,
            padding: '7px 10px',
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: T.text.secondary,
              fontFamily: T.mono,
              marginBottom: 2,
            }}
          >
            Timeline
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              fontFamily: T.mono,
              color: T.text.warning,
            }}
          >
            {s.timeline}
          </div>
        </div>
        <div
          style={{
            background: T.bg.raised,
            borderRadius: T.radius.sm,
            padding: '7px 10px',
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: T.text.secondary,
              fontFamily: T.mono,
              marginBottom: 2,
            }}
          >
            Budget
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              fontFamily: T.mono,
              color: T.text.success,
            }}
          >
            {s.budget}
          </div>
        </div>
      </div>
      {s.withinContingency && (
        <div style={{ marginTop: 8 }}>
          <Badge level="low">Within contingency</Badge>
        </div>
      )}
      {s.cautionBadge && (
        <div style={{ marginTop: 8 }}>
          <Badge level="medium">{s.cautionBadge}</Badge>
        </div>
      )}
    </div>
  );
};

// ─── Simulator ────────────────────────────────────────────────────────────────
const Simulator = ({ simComponents }) => {
  const SIM = simComponents || DEFAULT_SIM_COMPONENTS;
  const initState = SIM.reduce((acc, c) => ({ ...acc, [c.key]: true }), {
    extraRes: false,
    defer: false,
  });
  const [state, setState] = useState(initState);
  const toggle = (key) => setState((p) => ({ ...p, [key]: !p[key] }));

  const calc = () => {
    let eL = 0,
      eH = 0,
      tL = 0,
      tH = 0,
      bL = 0,
      bH = 0,
      cnt = 0;
    SIM.forEach((c) => {
      if (state[c.key]) {
        eL += c.effortL;
        eH += c.effortH;
        tL += c.tlL;
        tH += c.tlH;
        bL += c.budL;
        bH += c.budH;
        cnt++;
      }
    });
    if (state.extraRes) {
      tL = Math.round(tL * 0.7);
      tH = Math.round(tH * 0.7);
    }
    if (state.defer) {
      tL = 0;
      tH = 0;
    }
    const mid = Math.round((bL + bH) / 2);
    return {
      eL,
      eH,
      tL: Math.round(tL),
      tH: Math.round(tH),
      mid,
      cnt,
      withinBudget: mid <= 9500,
      pressure: eH > 50 ? 'High' : eH > 25 ? 'Medium' : eH > 0 ? 'Low' : 'None',
      saved: eH > 0 ? Math.max(0, Math.round((1 - eH / 63) * 100)) : 100,
      conf: Math.min(95, 50 + cnt * 9),
    };
  };

  const r = calc();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        padding: '16px 18px',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            color: T.text.secondary,
            fontFamily: T.mono,
            marginBottom: 10,
          }}
        >
          CR components
        </div>
        {SIM.map((c) => {
          const on = state[c.key];
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={c.key}
              onClick={() => toggle(c.key)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 9,
                padding: '9px 11px',
                border: `1px solid ${on ? 'rgba(59,130,246,0.4)' : T.border}`,
                background: on ? 'rgba(59,130,246,0.06)' : 'transparent',
                borderRadius: T.radius.md,
                cursor: 'pointer',
                marginBottom: 7,
              }}
            >
              <div
                style={{
                  width: 17,
                  height: 17,
                  borderRadius: 4,
                  flexShrink: 0,
                  border: `1.5px solid ${on ? T.text.info : T.border}`,
                  background: on ? 'rgba(59,130,246,0.2)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: on ? T.text.info : 'transparent',
                  fontSize: 11,
                  marginTop: 1,
                }}
              >
                ✓
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.text.primary }}>
                  {c.label}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: T.text.secondary,
                    marginTop: 2,
                  }}
                >
                  +{c.effortL}–{c.effortH} hrs
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: T.text.secondary,
            fontFamily: T.mono,
            marginBottom: 10,
          }}
        >
          Live impact calculation
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 10,
          }}
        >
          {[
            {
              label: 'Effort low',
              value: `${r.eL} hrs`,
              color:
                r.eL > 50
                  ? T.text.danger
                  : r.eL > 25
                    ? T.text.warning
                    : T.text.success,
            },
            {
              label: 'Effort high',
              value: `${r.eH} hrs`,
              color:
                r.eH > 50
                  ? T.text.danger
                  : r.eH > 25
                    ? T.text.warning
                    : T.text.success,
            },
            {
              label: 'Timeline',
              value: `${r.tL}–${r.tH}d`,
              color:
                r.tH > 7
                  ? T.text.danger
                  : r.tH > 3
                    ? T.text.warning
                    : T.text.success,
            },
            {
              label: 'Budget (est.)',
              value: `£${r.mid.toLocaleString()}`,
              color: r.withinBudget ? T.text.success : T.text.danger,
            },
          ].map((m) => (
            <MetricCard
              key={m.label}
              label={m.label}
              value={m.value}
              color={m.color}
            />
          ))}
        </div>
        <div
          style={{
            background: T.bg.surface,
            borderRadius: T.radius.md,
            padding: 14,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: T.text.secondary,
              fontFamily: T.mono,
              marginBottom: 8,
            }}
          >
            Scenario confidence
          </div>
          <BellCurve confidence={r.conf} />
          <div style={{ fontSize: 10, color: T.text.secondary, marginTop: 6 }}>
            {r.cnt} of {SIM.length} components selected
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Tab config ───────────────────────────────────────────────────────────────
// ─── Task detail modal (opens when clicking a task row) ──────────────────────
const TaskDetailModal = ({ task, onClose, onChatWithAgent, chatLoading }) => {
  if (!task) return null;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: T.bg.card,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius.lg,
          padding: 24,
          width: 480,
          maxWidth: '90vw',
          fontFamily: T.sans,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span
            style={{ fontFamily: T.mono, fontSize: 13, color: T.text.info }}
          >
            {task.id}
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.text.secondary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: T.text.primary,
            marginBottom: 16,
            lineHeight: 1.4,
          }}
        >
          {task.name}
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'Owner', value: task.owner },
            { label: 'Status', value: task.status },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', gap: 12 }}>
              <span
                style={{
                  fontSize: 11,
                  color: T.text.secondary,
                  width: 72,
                  flexShrink: 0,
                }}
              >
                {label}
              </span>
              <span style={{ fontSize: 12, color: T.text.primary }}>
                {value}
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 12 }}>
            <span
              style={{
                fontSize: 11,
                color: T.text.secondary,
                width: 72,
                flexShrink: 0,
              }}
            >
              Impact
            </span>
            <span
              style={{ fontSize: 12, color: T.text.primary, lineHeight: 1.5 }}
            >
              {task.impact}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span
              style={{
                fontSize: 11,
                color: T.text.secondary,
                width: 72,
                flexShrink: 0,
              }}
            >
              Severity
            </span>
            <Badge level={task.severity}>
              {task.severity.charAt(0).toUpperCase() + task.severity.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Chat with Agent footer */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onChatWithAgent}
            disabled={chatLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 8,
              border: '1px solid rgba(59,130,246,0.4)',
              background: chatLoading
                ? 'rgba(59,130,246,0.06)'
                : 'rgba(59,130,246,0.12)',
              color: chatLoading ? T.text.secondary : T.text.info,
              fontSize: 12,
              fontWeight: 500,
              cursor: chatLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!chatLoading)
                e.currentTarget.style.background = 'rgba(59,130,246,0.2)';
            }}
            onMouseLeave={(e) => {
              if (!chatLoading)
                e.currentTarget.style.background = 'rgba(59,130,246,0.12)';
            }}
          >
            {chatLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <MessageSquare size={12} />
            )}
            Chat with Agent
          </button>
        </div>
      </div>
    </div>
  );
};

const PhaseDetailModal = ({ phase, onClose, onChatWithAgent, chatLoading }) => {
  if (!phase) return null;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: T.bg.card,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius.lg,
          padding: 24,
          width: 480,
          maxWidth: '90vw',
          fontFamily: T.sans,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <span style={{ fontSize: 11, color: T.text.secondary }}>
            Phase detail
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.text.secondary,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: T.text.primary,
            marginBottom: 16,
            lineHeight: 1.4,
          }}
        >
          {phase.phase}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'Schedule', value: phase.schedule },
            { label: 'Delta', value: phase.delta },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', gap: 12 }}>
              <span
                style={{
                  fontSize: 11,
                  color: T.text.secondary,
                  width: 72,
                  flexShrink: 0,
                }}
              >
                {label}
              </span>
              <span
                style={{
                  fontFamily: T.mono,
                  fontSize: 12,
                  color: T.text.warning,
                }}
              >
                {value}
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 12 }}>
            <span
              style={{
                fontSize: 11,
                color: T.text.secondary,
                width: 72,
                flexShrink: 0,
              }}
            >
              Impact
            </span>
            <span
              style={{ fontSize: 12, color: T.text.primary, lineHeight: 1.5 }}
            >
              {phase.impact}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span
              style={{
                fontSize: 11,
                color: T.text.secondary,
                width: 72,
                flexShrink: 0,
              }}
            >
              Severity
            </span>
            <Badge level={phase.severity}>
              {phase.severity.charAt(0).toUpperCase() + phase.severity.slice(1)}
            </Badge>
          </div>
        </div>

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onChatWithAgent}
            disabled={chatLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 8,
              border: '1px solid rgba(59,130,246,0.4)',
              background: chatLoading
                ? 'rgba(59,130,246,0.06)'
                : 'rgba(59,130,246,0.12)',
              color: chatLoading ? T.text.secondary : T.text.info,
              fontSize: 12,
              fontWeight: 500,
              cursor: chatLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!chatLoading)
                e.currentTarget.style.background = 'rgba(59,130,246,0.2)';
            }}
            onMouseLeave={(e) => {
              if (!chatLoading)
                e.currentTarget.style.background = 'rgba(59,130,246,0.12)';
            }}
          >
            {chatLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <MessageSquare size={12} />
            )}
            Chat with Agent
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reusable tab-level chat-with-agent button ────────────────────────────────
const TabChatButton = ({ onClick, loading }) => (
  <div
    style={{
      marginTop: 12,
      paddingTop: 12,
      borderTop: `1px solid ${T.border}`,
      display: 'flex',
      justifyContent: 'flex-end',
    }}
  >
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px',
        borderRadius: 7,
        border: '1px solid rgba(59,130,246,0.35)',
        background: loading ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.10)',
        color: loading ? T.text.secondary : T.text.info,
        fontSize: 11,
        fontWeight: 500,
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!loading)
          e.currentTarget.style.background = 'rgba(59,130,246,0.18)';
      }}
      onMouseLeave={(e) => {
        if (!loading)
          e.currentTarget.style.background = 'rgba(59,130,246,0.10)';
      }}
    >
      {loading ? (
        <Loader2 size={11} className="animate-spin" />
      ) : (
        <MessageSquare size={11} />
      )}
      Chat with Agent
    </button>
  </div>
);

const TABS = [
  { key: 'tasks', label: 'Tasks', icon: '✓' },
  { key: 'modules', label: 'Modules', icon: '⬡' },
  { key: 'phases', label: 'Phases', icon: '◈' },
  { key: 'stakeholders', label: 'Stakeholders', icon: '◎' },
  { key: 'resources', label: 'Resources', icon: '◉' },
  { key: 'budget', label: 'Budget', icon: '£' },
  { key: 'timeline', label: 'Timeline', icon: '⏱' },
];

// ─── Main modal component ─────────────────────────────────────────────────────
export default function CRDetailModal({ cr, onClose }) {
  const navigate = useNavigate();
  const { stageCRChat } = useChangeRequests();
  const [activeTab, setActiveTab] = useState('tasks');
  const [section, setSection] = useState('impact'); // impact | scenarios | simulator
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const chatLoading = false; // staging is synchronous — no spinner needed

  const impact = cr?.impact || {};
  const metrics = cr?.metrics || {
    timeline: '+8–11d',
    effort: '+47–63h',
    budget: '£16–22K',
    confidence: '78%',
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Stage context for the chat input — user writes their message before sending
  const buildPayload = (sectionKey, sectionLabel, sectionData) => ({
    id: cr.id,
    source_type: 'change_request',
    event_name: `${cr.id}: ${cr.title}`,
    timestamp: new Date().toISOString(),
    active: true,
    cr_data: cr,
    ...(sectionKey
      ? {
          section_key: sectionKey,
          section_label: sectionLabel,
          section_data: sectionData,
        }
      : {}),
  });

  const handleChatWithAgent = () => {
    stageCRChat(buildPayload());
    onClose();
    navigate('/chat');
  };

  const handleChatWithSection = (sectionKey, sectionLabel, sectionData) => {
    stageCRChat(buildPayload(sectionKey, sectionLabel, sectionData));
    onClose();
    navigate('/chat');
  };

  const renderTabContent = () => {
    const tasks = impact?.tasks || DEFAULT_TASKS;
    const modules = impact?.modules || DEFAULT_MODULES;
    const phases = impact?.phases || DEFAULT_PHASES;
    const stakeholders = impact?.stakeholders || DEFAULT_STAKEHOLDERS;
    const resources = impact?.resources || DEFAULT_RESOURCES;
    const budgetRows = impact?.budgetRows || DEFAULT_BUDGET_ROWS;
    const timelineRows = impact?.timelineRows || DEFAULT_TIMELINE_ROWS;

    switch (activeTab) {
      case 'tasks':
        return (
          <TasksTab
            data={impact}
            onTaskClick={setSelectedTask}
            onChatWithAgent={() =>
              handleChatWithSection('tasks', 'Tasks', { tasks })
            }
            chatLoading={chatLoading}
          />
        );
      case 'modules':
        return (
          <ModulesTab
            data={impact}
            onChatWithAgent={() =>
              handleChatWithSection('modules', 'Modules', { modules })
            }
            chatLoading={chatLoading}
          />
        );
      case 'phases':
        return (
          <PhasesTab
            data={impact}
            onPhaseClick={setSelectedPhase}
            onChatWithAgent={() =>
              handleChatWithSection('phases', 'Phases', { phases })
            }
            chatLoading={chatLoading}
          />
        );
      case 'stakeholders':
        return (
          <StakeholdersTab
            data={impact}
            navigate={navigate}
            onChatWithAgent={() =>
              handleChatWithSection('stakeholders', 'Stakeholders', {
                stakeholders,
              })
            }
            chatLoading={chatLoading}
          />
        );
      case 'resources':
        return (
          <ResourcesTab
            data={impact}
            onChatWithAgent={() =>
              handleChatWithSection('resources', 'Resources', { resources })
            }
            chatLoading={chatLoading}
          />
        );
      case 'budget':
        return (
          <BudgetTab
            data={impact}
            onChatWithAgent={() =>
              handleChatWithSection('budget', 'Budget', { budgetRows })
            }
            chatLoading={chatLoading}
          />
        );
      case 'timeline':
        return (
          <TimelineTab
            data={impact}
            navigate={navigate}
            onChatWithAgent={() =>
              handleChatWithSection('timeline', 'Timeline', { timelineRows })
            }
            chatLoading={chatLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: T.sans,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          background: T.bg.page,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          overflow: 'hidden',
          width: '92vw',
          maxWidth: 1200,
          height: '90vh',
        }}
      >
        {/* ── Top bar ─────────────────────────────────────────────────────────── */}
        <div
          style={{
            background: T.bg.card,
            borderBottom: `1px solid ${T.border}`,
            padding: '10px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: T.mono,
              fontSize: 11,
            }}
          >
            <span style={{ color: T.text.info }}>SuperManager</span>
            <span style={{ color: T.text.muted }}>›</span>
            <span style={{ color: T.text.secondary }}>
              Change impact analysis
            </span>
            <span style={{ color: T.text.muted }}>›</span>
            <span style={{ color: T.text.warning }}>{cr?.id || 'CR'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge level="high">Assumption breached</Badge>
            <button
              onClick={handleChatWithAgent}
              disabled={chatLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid rgba(59,130,246,0.4)',
                background: chatLoading
                  ? 'rgba(59,130,246,0.06)'
                  : 'rgba(59,130,246,0.12)',
                color: chatLoading ? T.text.secondary : T.text.info,
                fontSize: 12,
                fontWeight: 500,
                cursor: chatLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!chatLoading)
                  e.currentTarget.style.background = 'rgba(59,130,246,0.2)';
              }}
              onMouseLeave={(e) => {
                if (!chatLoading)
                  e.currentTarget.style.background = 'rgba(59,130,246,0.12)';
              }}
            >
              {chatLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <MessageSquare size={13} />
              )}
              Chat with Agent
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.text.secondary,
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = T.text.primary)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = T.text.secondary)
              }
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ──────────────────────────────────────────────────── */}
        <div
          style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}
          className="cr-scroll"
        >
          {/* CR Header card */}
          <div
            style={{
              background: T.bg.card,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius.lg,
              overflow: 'hidden',
              marginBottom: 14,
            }}
          >
            <div
              style={{
                height: 3,
                background: 'linear-gradient(90deg, #3b82f6, #a78bfa, #f59e0b)',
              }}
            />
            <div style={{ padding: '16px 18px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: T.text.secondary,
                      fontFamily: T.mono,
                      marginBottom: 4,
                    }}
                  >
                    {cr?.id} · {cr?.classification} · {cr?.phase}
                  </div>
                  <div
                    style={{
                      fontSize: 19,
                      fontWeight: 500,
                      color: T.text.primary,
                      lineHeight: 1.3,
                      marginBottom: 8,
                    }}
                  >
                    {cr?.title}
                  </div>
                  <div style={{ fontSize: 12, color: T.text.secondary }}>
                    Raised by{' '}
                    <span style={{ color: T.text.primary }}>
                      {cr?.raisedBy}
                    </span>{' '}
                    · {cr?.date}
                    {cr?.assumption && (
                      <>
                        {' '}
                        ·{' '}
                        <span style={{ color: T.text.warning }}>
                          "{cr.assumption}"
                        </span>
                      </>
                    )}
                    {cr?.sourceDoc && (
                      <>
                        {' '}
                        —{' '}
                        <span style={{ color: T.text.secondary }}>
                          {cr.sourceDoc}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4,1fr)',
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <MetricCard
                  label="Timeline impact"
                  value={metrics.timeline}
                  color={T.text.danger}
                />
                <MetricCard
                  label="Effort impact"
                  value={metrics.effort}
                  color={T.text.danger}
                />
                <MetricCard
                  label="Budget delta"
                  value={metrics.budget}
                  color={T.text.danger}
                />
                <MetricCard
                  label="Confidence"
                  value={metrics.confidence}
                  color={T.text.info}
                />
              </div>
            </div>
          </div>

          {/* Section switcher */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[
              { key: 'impact', label: 'Full impact' },
              { key: 'scenarios', label: 'Scenarios' },
              { key: 'simulator', label: 'Simulator' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  border: `1px solid ${section === s.key ? 'rgba(59,130,246,0.5)' : T.border}`,
                  background:
                    section === s.key ? 'rgba(59,130,246,0.12)' : 'transparent',
                  color: section === s.key ? T.text.info : T.text.secondary,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* ── Full impact section ─── */}
          {section === 'impact' && (
            <div
              style={{
                background: T.bg.card,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius.lg,
                overflow: 'hidden',
              }}
            >
              {/* Tab bar */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: `1px solid ${T.border}`,
                  overflowX: 'auto',
                }}
              >
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    style={{
                      padding: '10px 16px',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      border: 'none',
                      borderBottom: `2px solid ${activeTab === t.key ? T.text.info : 'transparent'}`,
                      background: 'transparent',
                      color:
                        activeTab === t.key ? T.text.info : T.text.secondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>
              {/* Tab content */}
              <div style={{ padding: '16px 18px' }}>{renderTabContent()}</div>
            </div>
          )}

          {/* ── Scenarios section ─── */}
          {section === 'scenarios' && (
            <div>
              <div
                style={{
                  fontSize: 13,
                  color: T.text.secondary,
                  marginBottom: 14,
                  lineHeight: 1.6,
                }}
              >
                Three scoping options — choose the path before the client call.
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3,1fr)',
                  gap: 14,
                }}
              >
                {(impact?.scenarios || DEFAULT_SCENARIOS).map((s) => (
                  <ScenarioCard key={s.id} s={s} />
                ))}
              </div>
            </div>
          )}

          {/* ── Simulator section ─── */}
          {section === 'simulator' && (
            <div
              style={{
                background: T.bg.card,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius.lg,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '13px 18px',
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: T.text.secondary,
                    fontFamily: T.mono,
                    marginBottom: 2,
                  }}
                >
                  Interactive
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    color: T.text.primary,
                  }}
                >
                  Impact simulator
                </div>
              </div>
              <Simulator simComponents={impact?.simComponents} />
            </div>
          )}
        </div>
      </div>

      <style>{`
        .cr-scroll::-webkit-scrollbar { width: 5px; }
        .cr-scroll::-webkit-scrollbar-track { background: transparent; }
        .cr-scroll::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 4px; }
      `}</style>

      {/* Task detail modal — rendered inside the outer backdrop */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onChatWithAgent={() => {
            setSelectedTask(null);
            handleChatWithSection('task', `Task ${selectedTask.id}`, {
              task: selectedTask,
            });
          }}
          chatLoading={chatLoading}
        />
      )}

      {/* Phase detail modal */}
      {selectedPhase && (
        <PhaseDetailModal
          phase={selectedPhase}
          onClose={() => setSelectedPhase(null)}
          onChatWithAgent={() => {
            setSelectedPhase(null);
            handleChatWithSection('phase', selectedPhase.phase, {
              phase: selectedPhase,
            });
          }}
          chatLoading={chatLoading}
        />
      )}
    </div>
  );
}
